/**
 * **The instruction sub-forms' two chromes, as four functions rather than twenty
 * ternaries** (LF-6314, moved here from `property-instruction/constants.ts` when the
 * share instruction needed the same four).
 *
 * Both instruction sections — property and share — are several files and roughly a
 * dozen styled elements each, and only four style decisions actually differ between
 * the form chrome and the attribute treatment: the type of a sub-label, the ink of a
 * choice control, the ink of a choice *card*, and the panel that groups them.
 * Threading a `compact` flag into a `cn()` at each call site is how one decision ends
 * up written twenty times and then corrected in seventeen of them — which is not
 * hypothetical: the card palette *was* inlined twice inside the property section, and
 * the two copies had already drifted by one border shade before they were pulled back
 * together.
 *
 * It sits in `shared/` rather than in either section's folder because a share
 * component importing from `property-instruction/` would be a name that lies about
 * what it is reaching for.
 *
 * What compact deliberately does **not** change is the *shapes*. A multi-way choice
 * with icons has no arm in the treatment (its segmented boolean is two-way), and the
 * frontend rules' carve-out for a hand-rolled control is exactly this case — "the
 * design is deliberately distinct from every standard pattern". Ink and type change
 * so the block stops being the loudest thing on a quiet surface; the controls stay
 * what they are.
 */

import {
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK
} from '@/components/attributes';

/**
 * **The section's two chromes live in this block, as four functions rather than
 * twenty ternaries** (LF-6314).
 *
 * The instruction section is four files and roughly a dozen styled elements, and
 * only four style decisions actually differ between the form chrome and the
 * attribute treatment: the type of a sub-label, the ink of a choice control, the
 * ink of a choice *card*, and the panel that groups them. Threading a `compact`
 * flag into a `cn()` at each of the twelve call sites is how one decision ends up
 * written twelve times and then corrected in nine of them — which is not
 * hypothetical here: the card palette *was* inlined twice, and the two copies had
 * already drifted by one border shade before they were pulled back together.
 *
 * What compact deliberately does **not** change is the *shapes*. A multi-way choice
 * with icons has no arm in the treatment (its segmented boolean is two-way), and
 * the frontend rules' carve-out for a hand-rolled control is exactly this case —
 * "the design is deliberately distinct from every standard pattern". Ink and type
 * change so the block stops being the loudest thing on a quiet surface; the
 * controls stay what they are.
 */

/**
 * **One size for every choice button in the compact treatment** (VALE-355).
 *
 * The instruction pills, the transfer method, the transfer-to chips and the holding
 * type are four groups asking the same kind of question, and they were three
 * different heights: the pills `py-1`, the method cards `py-1.5`, and the chips —
 * being `ToggleGroupItem`s — silently inheriting `toggleVariants`' `default` size,
 * which is `h-10 px-3 min-w-10` (34px under this theme's `--spacing`). Review asked
 * for the instruction pill's size, everywhere, and only that size.
 *
 * `h-auto min-w-0` is what makes it stick on a `ToggleGroupItem`: without them the
 * base `cva` size wins and the padding here changes nothing.
 */
export const INSTRUCTION_CHOICE_SIZE =
  'text-12px h-auto min-w-0 rounded-md px-2 py-1';

/**
 * One choice **card** — the transfer method and the beneficiary holding type, and
 * (in compact) the instruction pills too, via {@link instructionChoiceClass}.
 *
 * A card marks a choice rather than reading as an action, so the active one is
 * *tinted* rather than filled.
 */
export function instructionCardClass(
  compact: boolean,
  isActive: boolean
): string {
  if (!compact) {
    return isActive
      ? 'border-foreground bg-surface-subtle font-medium text-foreground'
      : 'border-border text-foreground-secondary hover:border-border-strong';
  }
  /*
    **The compact palette, re-measured (VALE-355).**

    It used to be `border-slate-400 bg-slate-50` selected against
    `border-slate-200` unselected with a `hover:border-slate-300`, and reported as
    *"selected state is grey? unclear"*. Measured in the browser, the report is
    exactly right and for three compounding reasons:

    · **The fill was not a fill.** `slate-50` is #f8fafc against a white panel —
      `lab(98.14)` against `lab(100)`. At 12px chip size that is not a step a
      reader can see, so the tint carried none of the "chosen" signal it was
      supposed to.
    · **The only real cue was one border step, and hover sat on top of it.**
      slate-200 → slate-300 → slate-400 puts *hover* exactly between unselected
      and selected, so moving the mouse across the row made an unselected option
      read more selected than the selected one.
    · **On the `Transfer to` chips the two states were pixel-identical.** Those are
      `ToggleGroupItem`s, and the shared `toggleVariants` base carries
      `data-[state=on]:bg-accent` *and* `hover:bg-muted` — both of which resolve to
      the same `oklch(0.9702 0 0)` in this theme, and both of which outrank a plain
      `bg-slate-50` on specificity. So the chosen beneficiary and whichever chip
      the pointer happened to be over were the same colour.

    The fix is **no fill at all** — a border two steps clear of hover's
    (`slate-800`), the heavier weight, and a check glyph at the call sites
    ({@link InstructionChoiceMark}) so the state survives a screenshot, a
    colour-blind reader and a low-contrast display.

    A `slate-100` fill was tried on the way here and dropped on review: *"don't put
    the bg of the selected buttons in gray, keep the white — the outline and
    validate icon is enough."* That is the right call and it also settles the
    original problem more cleanly than a tint could. Once the mark carries *chosen*,
    a fill is a second, weaker signal saying the same thing — and any fill light
    enough not to shout was, by construction, close enough to hover's tint to be
    confusable. Selected is now the **only** state with no tint: unselected is
    white, hover is `slate-50`, selected is white with a dark outline and a tick.
    Nothing to mistake for anything else.

    **The `data-[state=on]:` and `hover:` arms are not belt-and-braces.** They are
    the only way to outrank the `toggleVariants` base above, and they live here
    rather than at the toggle call site so the palette has one definition — the
    same argument this whole module exists for.
  */
  return isActive
    ? 'border-foreground bg-card font-medium text-foreground hover:bg-card hover:text-foreground data-[state=on]:bg-card data-[state=on]:text-foreground'
    : 'border-border bg-card text-foreground-secondary hover:border-border-strong hover:bg-surface-subtle hover:text-foreground-secondary';
}

/** A sub-label inside the section — the treatment's label type in compact. */
export function instructionLabelClass(compact: boolean): string {
  return compact
    ? `${ATTRIBUTE_LABEL_TEXT} ${ATTRIBUTE_MUTED_INK}`
    : 'text-sm font-medium text-foreground-secondary';
}

/** One instruction **pill**: filled near-black in form chrome, tinted in compact. */
export function instructionChoiceClass(
  compact: boolean,
  isActive: boolean
): string {
  if (!compact) {
    return isActive
      ? 'border-foreground bg-foreground text-background'
      : 'border-border bg-card text-foreground-secondary hover:border-border-strong hover:bg-surface-subtle';
  }
  /*
    **In compact this is {@link instructionCardClass}** — the same tinted state, not
    a filled one, and that convergence is the point.

    Two corrections landed here in one go, both from an isolated visual review:
    a filled chip was "the highest-contrast element in the whole drawer" and read as
    a primary CTA rather than as a recorded choice (near-black first, then slate-700,
    and slate-700 was still the loudest thing on the panel); and pills-filled beside
    cards-tinted were "two competing selection idioms in the same block for the same
    kind of choice". A tint says *chosen* without shouting, and one idiom is one
    fewer thing for a reader to decode. The shapes still differ — a pill is a value,
    a card is a method — which is a difference of kind rather than of loudness.

    VALE-355 kept that convergence and fixed the tint itself — see
    {@link instructionCardClass}. A tint that a reader cannot see says nothing at
    all, which is the failure mode the quiet treatment had walked into.
  */
  return instructionCardClass(true, isActive);
}

/**
 * The panel that groups a sub-question — squarer and quieter in compact.
 *
 * **No fill in compact** (VALE-355). It was `bg-slate-50/60`, and the panel now
 * contains real attribute rows whose hover highlight is `slate-50` — so hovering a
 * beneficiary's share row produced no visible change at all, because the highlight
 * and the panel were the same colour. That is precisely the measurement
 * `attribute-read-out.tsx` records for why the cell's highlight is `slate-50` and
 * not `slate-100`: a tint that matches what sits behind it is not a tint.
 *
 * The border alone is enough to group the sub-question, and it is what the rows
 * inside need in order to be interactive.
 */
export function instructionPanelClass(compact: boolean): string {
  return compact
    ? 'rounded-md border border-border'
    : 'rounded-lg border border-border bg-surface-subtle/30';
}
